/**
 * Imports necessary dependencies and components for the current module.
 * @module
 * @requires React
 * @requires CSmartTable from "@coreui/react-pro"
 * @requires useTranslation from "react-i18next"
 * @requires Card from "react-bootstrap/Card"
 * @requires CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML"
 * @requires Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb"
 * @requires ConfirmModal from "../../components/confirmModal/confirmModel"
 * @requires EmptyCell from "../../components/emptyCell/emptyCell"
 * @requires CreateCategory from "./createCategory"
 */
import { CSmartTable } from "@coreui/react-pro";
import React from "react";
import Card from "react-bootstrap/Card";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../../components/confirmModal/confirmModel";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML";
import EmptyCell from "../../components/emptyCell/emptyCell";
import CreateCategory from "./createCategory";

/**
 * A component for managing categories.
 * @param {{object}} props - The component props.
 * @param {{function}} props.showCreateCategoryModal - Function to show the create category modal.
 * @param {{function}} props.closeCreateCategoryModal - Function to close the create category modal.
 * @param {{function}} props.openCreateCategoryModal - Function to open the create category modal.
 * @param {{array}} props.categoryData - The data for the categories.
 * @param {{array}} props.columns - The columns for the category table.
 * @param {{boolean}} props.placeHolderShimer - Flag indicating if the placeholder shimmer effect should be shown.
 * @param {{function}} props.onUpdateCategory - Function to update a
 */
const CategoryManagementComponent = (props) => {
  /**
   * Retrieves the translation function from the useTranslation hook.
   * @returns The translation function.
   */
  const { t } = useTranslation();

  /**
   * Destructures the properties from the given props object.
   * @param {{object}} props - The props object containing the properties to destructure.
   * @returns None
   */
  const {
    showCreateCategoryModal,
    closeCreateCategoryModal,
    openCreateCategoryModal,
    categoryData,
    columns,
    placeHolderShimer,
    onUpdateCategory,
    openDeleteUserModal,
    onTableFilterChange,
    NoDataTitle,
    NoDataDesc,
    ClearSearchButton,
    modalName,
    showDeleteUserModal,
    onDeleteUser,
    closeDeleteUserModal,
    smarttableref,
    NoDataFound,
    NoDataAddButtonTitle,
    EmptyDataAddButton,
    createCategoryModelLoaderButton,
    handleCategorytextChange,
    categorytextcount,
    createValues,
    createCategoryFunction,
    updateCategoryFunction,
    updateCategoryModelLoaderButton,

    dimensions,
    breadcrumbHeight,
    searchFilterClear,
    searchValue,
    loading,
    tableContainerRef,
    loaderHtml,
    handleSubmitCreate,
    categoryValue,
    itemsPerPage,
    pageTitle,
    closeUpdateCategoryModal,
    emptyDataDesc,
    emptyTitle,
    emptyImg,
    categoryList,
    deleteLoader,
    emptyDataDescTow,
    noDataDescTwo,
    navigateClick,
  } = props;

  return (
    <React.Fragment>
      <Helmet>
        <title>Category Management | RizzTix </title>
      </Helmet>
      <div className="pageContent categoryManagement">
        <div className="category">
          <Breadcrumbs
            innerPagetitle={t("CATEGORY_MANAGMENT")}
            breadcrumbItem={t("CATEGORY_MANAGMENT")}
            pageTitle={pageTitle}
            navigateClick={navigateClick}
            BreadcrumbInner={true}
            BreadcrumbmainTitle={true}
          />
          <Card className="mainCard mainCardWrapper">
            <Card.Body className="p-0 activeTab">
              <div className="mainCardBody">
                <div className="Card-Button">
                  <button
                    color="primary"
                    className="btn-block btn btn-primary fillButton"
                    onClick={() => openCreateCategoryModal()}
                  >
                    <i className="bx bxs-plus-circle"></i>
                    {t("ADD_CATEGORY")}
                  </button>
                </div>
                {/**
                 * Renders a smart table component with custom column rendering and filtering options.
                 * @param {{boolean}} cleaner - Whether to enable the cleaner feature.
                 * @param {{boolean}} clickableRows - Whether to make the rows clickable.
                 * @param {{Array}} columns - The array of column objects to display in the table.
                 * @param {{boolean}} columnFilter - Whether to enable column filtering.
                 * @param {{Array}} items - The array of items to display in the table.
                 * @param {{number}} itemsPerPage - The number of items to display per page.
                 * @param {{Object}} scopedColumns - An object mapping column names to custom rendering functions.
                 * @param {{function}} onTableFilterChange - The function to call
                 */}
                <CSmartTable
                  ref={smarttableref}
                  cleaner
                  clickableRows
                  categoryList={categoryList}
                  columns={columns}
                  columnFilter={false}
                  items={categoryData}
                  itemsPerPage={itemsPerPage}
                  scopedColumns={{
                    title: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item.title ? item?.title : "no category"}
                          </div>
                        </td>
                      );
                    },
                    eventcount: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item.eventcount ? item.eventcount : 0}
                          </div>
                        </td>
                      );
                    },
                    status: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="actionWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            <button
                              className="editButtton"
                              onClick={(e) => {
                                onUpdateCategory(e, item);
                              }}
                            >
                              <i
                                className="mdi mdi-pencil-outline font-size-24"
                                id="deletetooltip"
                              ></i>
                            </button>
                            <button
                              className="deleteButtton"
                              onClick={(e) => {
                                openDeleteUserModal(e, item);
                              }}
                            >
                              <i
                                className="bx bxs-trash font-size-24"
                                id="deletetooltip"
                              ></i>
                            </button>
                          </div>
                        </td>
                      );
                    },
                  }}
                  tableFilter
                  onTableFilterChange={onTableFilterChange}
                  tableFilterLabel={
                    searchValue?.length > 0 && (
                      <button
                        onClick={searchFilterClear}
                        className="btn-close"
                      ></button>
                    )
                  }
                  tableFilterPlaceholder={t("SEARCH_CATEGORY")}
                  tableProps={{
                    className: "mainTable",
                    responsive: true,
                    striped: false,
                    hover: false,
                  }}
                  noItemsLabel={
                    !placeHolderShimer && (
                      <EmptyCell
                        emptyItems={categoryData}
                        noDataTitle={
                          categoryData?.length === 0 ? NoDataFound : NoDataTitle
                        }
                        noDataAddButton={
                          categoryData?.length === 0 ? true : false
                        }
                        noDataAddButtonTitle={NoDataAddButtonTitle}
                        noDataDesc={NoDataDesc}
                        noDataDescTwo={noDataDescTwo}
                        emptyDataAddButton={EmptyDataAddButton}
                        clearSearchButton={ClearSearchButton}
                        emptyImg={emptyImg}
                        emptyTitle={emptyTitle}
                        emptyDataDesc={emptyDataDesc}
                        emptyDataDescTow={emptyDataDescTow}
                      />
                    )
                  }
                  tableBodyProps={{
                    className: "align-middle Table-Body-scroll",
                    style: {
                      height:
                        dimensions.width > 1279
                          ? dimensions.height -
                            breadcrumbHeight -
                            (loading && loaderHtml) -
                            250
                          : dimensions.height -
                            breadcrumbHeight -
                            (loading && loaderHtml) -
                            350,
                    },
                    ref: tableContainerRef,
                  }}
                />
                {loading && <CustomLoadingHTML />}
              </div>
            </Card.Body>
          </Card>
        </div>
        {/* Delete Message Model */}
        <ConfirmModal
          modalTitle={"Delete"}
          modalDescription={t("DELETE_HEADING")}
          isUnderline={false}
          showModal={showDeleteUserModal}
          leftBtnTitle={t("YES_DELETE")}
          isModelIcon={true}
          iconColor={"bx bxs-trash"}
          iconCricle={"iconCricleDelete"}
          leftBtnClass={"ok_Btn"}
          isFirstButton={true}
          isSecondButton={true}
          rightBtnClass="borderButton"
          rightBtnTitle={t("NO_CANCEL")}
          onProcced={(e, item) => {
            onDeleteUser(e, item);
          }}
          rightButtonClick={closeDeleteUserModal}
          loaderButtonShow={deleteLoader}
        />
        <CreateCategory
          placeholder={t("CATEGORY_PLACEHOLDER")}
          label={t("CATEGORY_NAME")}
          categoryValue={categoryValue}
          modalName={modalName}
          showCreateCategoryModal={showCreateCategoryModal}
          closeCreateCategoryModal={closeCreateCategoryModal}
          closeUpdateCategoryModal={closeUpdateCategoryModal}
          updateCategoryFunction={(form) => updateCategoryFunction(form)}
          createCategoryModelLoaderButton={createCategoryModelLoaderButton}
          handleCategorytextChange={handleCategorytextChange}
          categorytextcount={categorytextcount}
          updateCategoryModelLoaderButton={updateCategoryModelLoaderButton}
          createValues={createValues}
          createCategoryFunction={createCategoryFunction}
          handleSubmitCreate={handleSubmitCreate}
        />
      </div>
    </React.Fragment>
  );
};

export default CategoryManagementComponent;
